import { useEffect, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AdvisorCallbackForm } from '@/components/AdvisorCallbackForm/AdvisorCallbackForm';
import { GoBackArrowBtn } from '@/components/GoBackArrowBtn/GoBackArrowBtn';
import { Loader } from '@/components/Loader/loader';
import Toast from '@/components/Toast/Toast';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import useScreenSize from '@/hooks/useScreenSize';
import { useSendRappelConseiller } from '@/hooks/useSendRappelConseiller';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { useGetRappelConseiller } from '@/services/getRappelConseiller/getRappelConseillerService';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

export const RappelConseiller: React.FC = () => {
  const { windowWidth } = useScreenSize();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const sendRappel = useSendRappelConseiller();
  const { data: rappelData, isLoading, isError } = useGetRappelConseiller();

  const [validateRappelLoading, setValidateRappelLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastDescription, setToastDescription] = useState<string | null>(null);

  const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  const initialPhone = sessionUser?.telephone ?? '';
  const initialPhoneValid = initialPhone !== '';

  const handleFormSubmit = (formData: FormData) => {
    setValidateRappelLoading(true);

    if (!user) {
      setValidateRappelLoading(false);
      return;
    }

    formData.append('nomRappel', user.nom ?? '');
    formData.append('prenomRappel', user.prenom ?? '');
    formData.append('typeContactRappel', user.type ?? '');
    formData.append('idSFRappel', user.id ?? '');
    formData.append('emailRappel', user.email ?? '');
    formData.append('cpRappel', user.codePostal ?? '');
    formData.append('g-recaptcha-response', '');

    sendRappel.mutate(formData, {
      onSuccess: () => {
        setValidateRappelLoading(false);
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'valider le rappel',
          event_detail_1: 'cta',
          event_statut: 'ok',
        });
        navigate(ROUTE.VALIDATIONRAPPELCONSEILLER, {
          state: { fromRappelConseiller: true },
        });
      },
      onError: (error) => {
        log.error(`RappelConseiller: Error sending rappel: ${error}`);
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'valider le rappel',
          event_detail_1: 'cta',
          event_statut: 'ko',
        });

        setValidateRappelLoading(false);
        setToastMessage('Une erreur technique est survenue');
        setToastDescription('Veuillez réessayer.');

        navigate(ROUTE.ERREUR);
      },
    });
  };

  const handleToastClose = () => {
    setToastMessage(null);
  };

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'besoin d aide',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'besoin d aide',
        event_page: 'besoin d aide',
      });
    }
  }, [user?.id, user?.type, user?.success]);

  if (isLoading) {
    return <Loader />;
  }
  if (isError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.HELP } });

  return (
    <>
      {validateRappelLoading && <Loader />}

      {toastMessage && <Toast message={toastMessage} description={toastDescription || ''} onClose={handleToastClose} />}
      <MtxGrid className={styles.container}>
        {rappelData ? (
          <>
            <MtxGrid row>
              <MtxGrid col={12} offset={0}>
                {windowWidth < 900 && <GoBackArrowBtn parent={{ path: ROUTE.HELP }} />}

                <h1 className={styles.title}>{t('helpContact.title')}</h1>

                <h3 className={styles.subtitle}>
                  {t('helpContact.wording')}
                  {windowWidth < 600 && <br />} {t('helpContact.callbackSuggestion')}
                </h3>
              </MtxGrid>
            </MtxGrid>
            <AdvisorCallbackForm
              data={rappelData}
              initialPhone={initialPhone}
              initialPhoneValid={initialPhoneValid}
              onSubmit={handleFormSubmit}
              isLoading={validateRappelLoading}
              scrollAnchorId="rappel-conseiller-anchor-telephone"
            />
          </>
        ) : (
          <Loader />
        )}
      </MtxGrid>
    </>
  );
};
